import { EllipsisVertical, Info } from "lucide-react";

import { Tag } from "@/design-system/components/Tag/Tag";
import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";
import { Banner } from "@/design-system/components/Banner/Banner";
import { DropdownMenu } from "@/design-system/components/DropdownMenu/DropdownMenu";
import { FunnelEvents } from "@/api/insights";
import { getDestinationIcon } from "../utils";

interface EventDetailsPanelProps {
  focusedOption: FunnelEvents | undefined;
  hasEvents: boolean;
}

export function EventDetailsPanel({
  focusedOption,
  hasEvents,
}: EventDetailsPanelProps) {
  return (
    <div className="relative flex w-1/2 flex-col gap-4 p-3">
      <DropdownMenu
        getOptionLabel={(opt) => opt}
        icon={<EllipsisVertical size={20} className="text-iconDefault" />}
        options={["View event details"]}
        getOptionUrl={() =>
          focusedOption
            ? `/events/library/${focusedOption.eventDefId}`
            : undefined
        }
        getIconAfter={() => true}
        triggerProps={{
          className: "absolute right-1 top-1 h-8 w-8 p-1",
        }}
        contentProps={{
          className: "relative !z-[80]",
        }}
      />
      {focusedOption ? (
        <>
          <div className="flex flex-col gap-3">
            <div className="mr-4 flex flex-col break-words">
              <span className="text-lg font-bold">{focusedOption.name}</span>
              <span className="text-textSubtle text-xs">
                {focusedOption.type}
              </span>
            </div>

            {focusedOption.tags &&
              Object.keys(focusedOption.tags).length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  {Object.keys(focusedOption.tags).map(
                    (tag) =>
                      focusedOption.tags?.[tag] && <Tag key={tag}>{tag}</Tag>,
                  )}
                </div>
              )}
          </div>
          <div className="flex min-h-0 flex-col gap-1 overflow-auto">
            <span className="text-textSubtle text-xs">Description</span>
            <p className="text-sm leading-5">
              {focusedOption.description || (
                <span className="text-textSubtle text-xs italic">
                  No description
                </span>
              )}
            </p>
          </div>
          {focusedOption.isOther && (
            <Banner kind="note" type="warning" className="p-2">
              <ul>
                {focusedOption.adPlatformsDelivered === 0 &&
                  !focusedOption.hasNoRecentTraffic && (
                    <li>This event is not delivered to any ad platforms.</li>
                  )}
                {focusedOption.isHighVolumeEvent &&
                  (focusedOption.isSpecialCaseAdsDelivPv ? (
                    <li>
                      This is a high-volume event for a known non-conversion
                      scenario.
                    </li>
                  ) : (
                    <li>
                      This is a high-volume event not likely to be a mid or low
                      conversion.
                    </li>
                  ))}
                {focusedOption.hasNoRecentTraffic && (
                  <li>This event has no recent traffic.</li>
                )}
                {focusedOption.isSpecialCaseAdsDelivPv && (
                  <li>This a known non-conversion scenario.</li>
                )}
              </ul>
            </Banner>
          )}
          <div className="flex min-h-0 flex-col gap-1 overflow-auto">
            <span className="text-textSubtle flex gap-1 text-xs">
              Events delivered last 30D
              <Tooltip
                contentProps={{ className: "relative !z-[80]" }}
                content="Only counts event instances delivered to supported integrations in the last 30 days"
              >
                <Info size={16} />
              </Tooltip>
            </span>
            {focusedOption.totalEventsDelivered ? (
              <h3>{focusedOption.totalEventsDelivered?.toLocaleString()}</h3>
            ) : (
              <span className="text-textSubtle text-xs italic">
                No delivered events found
              </span>
            )}
          </div>
          <div className="flex min-h-0 flex-col gap-1 overflow-auto">
            <span className="text-textSubtle flex gap-1 text-xs">
              Integrations
            </span>
            {focusedOption.adPlatformsDelivered > 0 &&
            focusedOption.adPlatformsDeliveredList ? (
              <div className="flex flex-wrap gap-2">
                {focusedOption.adPlatformsDeliveredList
                  .split(",")
                  .map((platform) => {
                    const trimmedPlatform = platform.trim();
                    const displayName = trimmedPlatform
                      .replace(/_/g, " ")
                      .replace(/^\w/, (c) => c.toUpperCase());
                    const Icon = getDestinationIcon(trimmedPlatform);
                    return (
                      <Tooltip key={trimmedPlatform} content={displayName}>
                        <span className="bg-surface-muted flex h-8 w-8 items-center justify-center rounded-full p-1.5">
                          {Icon ? (
                            <Icon className="h-6 w-6" />
                          ) : (
                            <span className="text-xs">
                              {trimmedPlatform.charAt(0)}
                            </span>
                          )}
                        </span>
                      </Tooltip>
                    );
                  })}
              </div>
            ) : (
              <span className="text-textSubtle text-xs italic">
                No integrations found
              </span>
            )}
          </div>
        </>
      ) : (
        hasEvents && (
          <div className="text-textSubtle flex h-full items-center justify-center text-sm">
            Hover over an event to see details
          </div>
        )
      )}
    </div>
  );
}
